import { useParams, Link } from 'react-router-dom';
import { categories, getArticlesByCategory, getLatestArticles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];
  const currentCategory = categories.find((c) => c.slug === slug);
  const latestArticles = getLatestArticles(3);

  const categoryNameMap: Record<string, string> = {
    'quick-start': '快速入门',
    'layout-design': '布局设计',
    'custom-theme': '主题定制',
    'responsive': '响应式适配',
    'advanced': '高级技巧',
  };

  if (!currentCategory || categoryArticles.length === 0) {
    return (
      <>
        <PageMeta title="分类未找到" description="抱歉，您访问的分类页面不存在。" />
        <ScrollToTop />
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">分类未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的分类页面不存在。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const catName = categoryNameMap[slug] || currentCategory.name;
  const schemaJson = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bluenav.cn' },
          { '@type': 'ListItem', position: 2, name: catName, item: `https://bluenav.cn/category/${slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${catName} - 蓝色小导航`,
        description: currentCategory.description,
        url: `https://bluenav.cn/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={catName}
        description={currentCategory.description}
        keywords={`蓝色小导航,${catName},导航栏设计,导航组件`}
        canonicalUrl={`https://bluenav.cn/category/${slug}`}
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: catName },
          ]}
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">{catName}</h1>
              <p className="text-foreground-500 text-sm md:text-base">{currentCategory.description}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group rounded-2xl bg-background-100/70 border border-background-200/60 overflow-hidden hover:border-primary-200/60 transition-all duration-300 hover:translate-y-[-4px]"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h2 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h2>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.tags.map((tag) => (
                        <span key={tag} className="text-xs text-secondary-600 bg-secondary-50 px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {latestArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100/60">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">最新文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {latestArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group p-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-200"
                  >
                    <h3 className="text-sm font-medium text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{article.date} · {article.readTime}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}